import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/Badge';
import {
  getDistributorConfig,
  saveDistributorConfig,
  testDistributorConnection,
  DistributorConfig,
} from '../services/distributorService';
import {
  Zap,
  Eye,
  EyeOff,
  CheckCircle,
  XCircle,
  AlertCircle,
  Package,
  ArrowRight,
  Info,
} from 'lucide-react';

type ConnectionStatus = 'connected' | 'not_configured' | 'error' | 'syncing';

function ConnectionStatusIndicator({ status }: { status: ConnectionStatus }) {
  const config = {
    connected: { dotColor: 'bg-emerald-500', label: 'Connected', variant: 'success' as const },
    not_configured: { dotColor: 'bg-slate-300', label: 'Not Configured', variant: 'secondary' as const },
    error: { dotColor: 'bg-rose-500', label: 'Error', variant: 'destructive' as const },
    syncing: { dotColor: 'bg-amber-500', label: 'Syncing...', variant: 'warning' as const },
  };

  const { dotColor, label, variant } = config[status];

  return (
    <Badge variant={variant} className="gap-1.5">
      <span className={`h-1.5 w-1.5 rounded-full ${dotColor} animate-pulse`} />
      {label}
    </Badge>
  );
}

export function IntegrationsView() {
  const navigate = useNavigate();
  const [config, setConfig] = useState<DistributorConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [apiToken, setApiToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  // Auto-dismiss messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const configData = await getDistributorConfig();
      setConfig(configData);
    } catch (err: any) {
      setError(err.message || 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveApiKey = async () => {
    if (!apiToken.trim()) {
      setError('API token is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await saveDistributorConfig(apiToken);
      setSuccess('API key saved successfully');
      setApiToken('');
      await loadConfig();
    } catch (err: any) {
      setError(err.message || 'Failed to save API key');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!apiToken.trim()) {
      setError('Enter an API token to test');
      return;
    }

    try {
      setTesting(true);
      setError(null);
      setTestResult(null);
      const result = await testDistributorConnection(apiToken);
      setTestResult(result);
    } catch (err: any) {
      setTestResult({
        success: false,
        message: err.message || 'Connection test failed',
      });
    } finally {
      setTesting(false);
    }
  };

  const getStatus = (): ConnectionStatus => {
    if (!config?.apiToken) return 'not_configured';
    if (config.lastSyncStatus === 'error') return 'error';
    return 'connected';
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="w-10 h-10 rounded-full border-4 border-slate-200 border-t-orange-600 animate-spin mx-auto" />
          <p className="text-sm text-slate-500 mt-4">Loading integrations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900">Integrations</h3>
          <p className="text-sm text-slate-500 mt-1">
            Connect to distributors to sync product pricing and availability.
          </p>
        </div>
        <div className="p-6">
          {/* Alerts */}
          {error && (
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 flex items-start gap-3 mb-6">
              <AlertCircle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-medium text-rose-800">Error</h4>
                <p className="text-sm text-rose-700 mt-0.5">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="text-rose-600 hover:text-rose-800 transition-colors text-xl leading-none"
                title="Dismiss error"
                aria-label="Dismiss error"
              >
                &times;
              </button>
            </div>
          )}

          {success && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3 mb-6">
              <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-medium text-emerald-800">Success</h4>
                <p className="text-sm text-emerald-700 mt-0.5">{success}</p>
              </div>
              <button
                onClick={() => setSuccess(null)}
                className="text-emerald-600 hover:text-emerald-800 transition-colors text-xl leading-none"
                title="Dismiss"
                aria-label="Dismiss"
              >
                &times;
              </button>
            </div>
          )}

          {/* Dicker Data Integration Card */}
          <div className="border border-slate-200 rounded-xl overflow-hidden">
            <div className="p-5 border-b border-slate-200 bg-slate-50/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center shadow-sm">
                    <Zap className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-slate-900">Dicker Data</h2>
                    <p className="text-sm text-slate-500">B2B API for pricing and stock</p>
                  </div>
                </div>
                <ConnectionStatusIndicator status={getStatus()} />
              </div>
            </div>

            <div className="p-5 space-y-6">
              {/* Connection Stats */}
              {config?.apiToken && (
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Last Sync
                    </p>
                    <p className="text-sm font-semibold text-slate-900 mt-1.5">
                      {formatDate(config.lastSyncAt)}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Products Synced
                    </p>
                    <p className="text-sm font-semibold text-slate-900 mt-1.5">
                      {config.productCount ?? 0}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Sync Status
                    </p>
                    <p className="text-sm font-semibold text-slate-900 mt-1.5">
                      {config.lastSyncStatus || 'Ready'}
                    </p>
                  </div>
                </div>
              )}

              {/* API Token Configuration */}
              <div className="space-y-3">
                <Label htmlFor="api-token">API Token</Label>
                <div className="flex gap-3">
                  <div className="flex-1 relative">
                    <Input
                      id="api-token"
                      type={showToken ? 'text' : 'password'}
                      value={apiToken}
                      onChange={(e) => setApiToken(e.target.value)}
                      placeholder={
                        config?.apiToken
                          ? '••••••••••••••••'
                          : 'Enter your Dicker Data API token'
                      }
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowToken(!showToken)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {showToken ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  <Button
                    onClick={handleTestConnection}
                    variant="outline"
                    disabled={testing || !apiToken.trim()}
                    loading={testing}
                  >
                    {testing ? 'Testing...' : 'Test Connection'}
                  </Button>
                  <Button
                    onClick={handleSaveApiKey}
                    disabled={saving || !apiToken.trim()}
                    loading={saving}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
                {config?.apiToken && (
                  <p className="text-xs text-slate-500">
                    An API key is already configured. Enter a new key to replace it.
                  </p>
                )}
              </div>

              {/* Test Result */}
              {testResult && (
                <div
                  className={`p-4 rounded-xl border ${
                    testResult.success
                      ? 'bg-emerald-50 border-emerald-200'
                      : 'bg-rose-50 border-rose-200'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {testResult.success ? (
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-rose-600" />
                    )}
                    <span
                      className={`text-sm font-medium ${
                        testResult.success ? 'text-emerald-800' : 'text-rose-800'
                      }`}
                    >
                      {testResult.message}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Manage Products Banner */}
      {config?.apiToken && (
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                <Package className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900">
                  Manage Products & SKUs
                </h3>
                <p className="text-sm text-slate-600">
                  Track SKUs, sync products, and import to your catalogue from the Marketplace.
                </p>
              </div>
            </div>
            <Button onClick={() => navigate('/marketplace?tab=dickerdata')}>
              Go to Dicker Data Tab
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-sky-50 border border-sky-200 rounded-xl p-5">
        <div className="flex gap-3">
          <Info className="w-5 h-5 text-sky-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-sky-800">
              About Dicker Data Integration
            </h4>
            <p className="text-sm text-sky-700 mt-1">
              Connect your Dicker Data B2B API to automatically sync product pricing and stock
              availability. Products synced from distributors will be marked as "Dicker Data"
              source in your catalogues, and pricing will refresh daily. Contact Dicker Data to
              obtain your API token.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default IntegrationsView;
